import React, { Component } from "react";
import {
  Alert,
  Button,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  Grid as Container,
  Modal,
  Row,
  Table
} from "react-bootstrap";

import Card from "components/Card/Card.jsx";
import ViewportAlert from "components/ViewportAlert/ViewportAlert";

const API_URL = "https://hgsystem.pl/mangusta/firmware_admin.php";

class Firmware extends Component {
  state = {
    releases: [],
    devices: [],
    assignments: [],
    loading: false,
    action: "",
    error: "",
    success: "",
    firmwareFile: null,
    manifestFile: null,
    notes: "",
    selectedReleaseId: "",
    selectedDeviceIds: [],
    selectedAssignmentIds: [],
    rolloutMode: "normal",
    urgentConfirmation: "",
    showUrgentConfirmation: false,
    pairingDeviceId: "",
    pairingCode: "",
    pairingExpiresAt: ""
  };

  componentDidMount() {
    this.loadData();
  }

  parseResponse = response =>
    response.text().then(text => {
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        throw new Error("Serwer zwrócił nieprawidłową odpowiedź.");
      }
      if (!response.ok || data.success === false)
        throw new Error(data.error || data.message || "Wystąpił błąd serwera.");
      return data;
    });

  post = body =>
    fetch(API_URL, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        auth: this.props.adminCredentials,
        ...body
      })
    }).then(this.parseResponse);

  collection = (data, name) => {
    if (Array.isArray(data[name])) return data[name];
    if (data.data && Array.isArray(data.data[name])) return data.data[name];
    return [];
  };

  loadData = () => {
    if (!this.props.adminCredentials) {
      this.setState({ error: "Brak uprawnień administratora." });
      return;
    }

    this.setState({ loading: true, error: "" });
    this.post({ action: "list" })
      .then(data => {
        const releases = this.collection(data, "releases");
        const devices = this.collection(data, "devices");
        const assignments = this.collection(data, "assignments");
        this.setState(previousState => ({
          releases,
          devices,
          assignments,
          loading: false,
          selectedReleaseId:
            previousState.selectedReleaseId ||
            (releases[0] ? String(this.releaseId(releases[0])) : ""),
          pairingDeviceId:
            previousState.pairingDeviceId ||
            (devices[0] ? String(this.deviceId(devices[0])) : ""),
          selectedDeviceIds: previousState.selectedDeviceIds.filter(id =>
            devices.some(device => String(this.deviceId(device)) === id)
          ),
          selectedAssignmentIds: previousState.selectedAssignmentIds.filter(
            id =>
              assignments.some(
                assignment => String(this.assignmentId(assignment)) === id
              )
          )
        }));
      })
      .catch(error =>
        this.setState({ loading: false, error: error.message })
      );
  };

  releaseId = release =>
    release.id !== undefined ? release.id : release.release_id;

  deviceId = device =>
    device.id !== undefined ? device.id : device.device_id;

  assignmentId = assignment =>
    assignment.id !== undefined ? assignment.id : assignment.assignment_id;

  releaseName = release => {
    const version = release.version || release.firmware_version || "bez wersji";
    const build = release.build || release.build_number || release.build_id;
    return build ? `${version} (build ${build})` : version;
  };

  deviceName = device =>
    device.desc ||
    device.description ||
    device.name ||
    `Urządzenie ${this.deviceId(device)}`;

  formatSize = value => {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes < 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(2)} MB`;
  };

  formatDate = value => {
    if (value === null || value === undefined || value === "") return "—";
    const text = String(value);
    const date = /^\d+$/.test(text)
      ? new Date(Number(text) * 1000)
      : new Date(`${text.replace(" ", "T")}Z`);
    return Number.isNaN(date.getTime())
      ? text
      : date.toLocaleString("pl-PL");
  };

  shortHash = value => {
    if (!value) return "—";
    const hash = String(value);
    return hash.length > 20 ? `${hash.slice(0, 12)}…${hash.slice(-8)}` : hash;
  };

  statusLabel = status => {
    const key = String(status || "").toLowerCase();
    const labels = {
      active: "Aktywne",
      published: "Opublikowane",
      ready: "Gotowe",
      pending: "Oczekuje",
      queued: "W kolejce",
      downloading: "Pobieranie",
      installing: "Instalowanie",
      completed: "Zakończone",
      success: "Sukces",
      failed: "Błąd",
      error: "Błąd",
      cancelled: "Anulowane",
      canceled: "Anulowane",
      paused: "Wstrzymane"
    };
    return labels[key] || status || "—";
  };

  isCancellable = assignment => {
    const status = String(assignment.status || "").toLowerCase();
    return (
      ["completed", "success", "cancelled", "canceled"].indexOf(status) < 0
    );
  };

  toggleSelection = (stateName, value) => {
    const id = String(value);
    this.setState(previousState => {
      const selected = previousState[stateName].slice();
      const index = selected.indexOf(id);
      if (index >= 0) selected.splice(index, 1);
      else selected.push(id);
      return { [stateName]: selected };
    });
  };

  selectAllDevices = () => {
    this.setState(previousState => ({
      selectedDeviceIds:
        previousState.selectedDeviceIds.length === previousState.devices.length
          ? []
          : previousState.devices.map(device => String(this.deviceId(device)))
    }));
  };

  publish = event => {
    event.preventDefault();
    const { firmwareFile, manifestFile } = this.state;
    if (!firmwareFile || !manifestFile) {
      this.setState({
        error: "Wybierz plik firmware BIN oraz podpisany manifest JSON."
      });
      return;
    }
    if (!/\.bin$/i.test(firmwareFile.name) || !/\.json$/i.test(manifestFile.name)) {
      this.setState({
        error: "Firmware musi być plikiem .bin, a manifest plikiem .json."
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const manifest = JSON.parse(reader.result);
        const signingData =
          manifest.signature_base64 ||
          manifest.signature ||
          manifest.signatures ||
          manifest.sig ||
          manifest.firmware_signature ||
          (manifest.firmware && manifest.firmware.signature);
        if (
          manifest.schema !== 1 ||
          typeof manifest.payload_base64 !== "string" ||
          !manifest.payload_base64 ||
          typeof signingData !== "string" ||
          !signingData
        )
          throw new Error(
            "Manifest nie ma poprawnego formatu podpisanego wydania OTA."
          );
      } catch (error) {
        this.setState({ error: error.message || "Nieprawidłowy manifest JSON." });
        return;
      }

      const formData = new FormData();
      formData.append("action", "publish");
      formData.append("auth", JSON.stringify(this.props.adminCredentials));
      formData.append("firmware", firmwareFile);
      formData.append("manifest", manifestFile);
      if (this.state.notes.trim())
        formData.append("notes", this.state.notes.trim());

      this.setState({ action: "publish", error: "", success: "" });
      fetch(API_URL, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData
      })
        .then(this.parseResponse)
        .then(() => {
          this.setState({
            action: "",
            firmwareFile: null,
            manifestFile: null,
            notes: "",
            success: "Wydanie zostało opublikowane."
          });
          if (this.firmwareInput) this.firmwareInput.value = "";
          if (this.manifestInput) this.manifestInput.value = "";
          this.loadData();
        })
        .catch(error => this.setState({ action: "", error: error.message }));
    };
    reader.onerror = () =>
      this.setState({ error: "Nie udało się odczytać manifestu." });
    reader.readAsText(manifestFile);
  };

  requestAssignment = event => {
    event.preventDefault();
    if (!this.state.selectedReleaseId) {
      this.setState({ error: "Wybierz wydanie firmware." });
      return;
    }
    if (this.state.selectedDeviceIds.length === 0) {
      this.setState({
        error:
          "Wybierz co najmniej jedno urządzenie. Wybór jest celowo jawny, aby ułatwić wdrożenia canary."
      });
      return;
    }
    if (this.state.rolloutMode === "urgent") {
      this.setState({
        showUrgentConfirmation: true,
        urgentConfirmation: "",
        error: ""
      });
      return;
    }
    this.assign();
  };

  assign = () => {
    this.setState({
      action: "assign",
      error: "",
      success: "",
      showUrgentConfirmation: false
    });
    this.post({
      action: "assign",
      release_id: /^\d+$/.test(this.state.selectedReleaseId)
        ? Number(this.state.selectedReleaseId)
        : this.state.selectedReleaseId,
      device_ids: this.state.selectedDeviceIds.map(id =>
        /^\d+$/.test(id) ? Number(id) : id
      ),
      mode: this.state.rolloutMode
    })
      .then(() => {
        this.setState({
          action: "",
          selectedDeviceIds: [],
          rolloutMode: "normal",
          urgentConfirmation: "",
          success: "Wdrożenie zostało zaplanowane."
        });
        this.loadData();
      })
      .catch(error => this.setState({ action: "", error: error.message }));
  };

  cancelAssignments = assignmentIds => {
    const ids = assignmentIds.map(String);
    if (ids.length === 0) {
      this.setState({ error: "Wybierz przypisania do wstrzymania." });
      return;
    }
    if (
      !window.confirm(
        `Wstrzymać i anulować ${ids.length} wybranych przypisań OTA?`
      )
    )
      return;

    this.setState({ action: "cancel", error: "", success: "" });
    this.post({
      action: "cancel",
      assignment_ids: ids.map(id => (/^\d+$/.test(id) ? Number(id) : id))
    })
      .then(() => {
        this.setState({
          action: "",
          selectedAssignmentIds: [],
          success: "Wybrane przypisania zostały wstrzymane/anulowane."
        });
        this.loadData();
      })
      .catch(error => this.setState({ action: "", error: error.message }));
  };

  generatePairing = event => {
    event.preventDefault();
    if (!this.state.pairingDeviceId) {
      this.setState({ error: "Wybierz urządzenie." });
      return;
    }
    this.setState({
      action: "pairing",
      pairingCode: "",
      pairingExpiresAt: "",
      error: "",
      success: ""
    });
    const deviceId = this.state.pairingDeviceId;
    this.post({
      action: "generate_pairing_code",
      device_id: /^\d+$/.test(deviceId) ? Number(deviceId) : deviceId
    })
      .then(data => {
        const pairing = data.pairing || data.data || data;
        this.setState({
          action: "",
          pairingCode: pairing.pairing_code || pairing.code || "",
          pairingExpiresAt:
            pairing.expires_at || pairing.expires || pairing.expiry || ""
        });
      })
      .catch(error => this.setState({ action: "", error: error.message }));
  };

  renderReleaseTable() {
    return (
      <Table striped hover>
        <thead>
          <tr>
            <th>Wersja / build</th>
            <th>Sprzęt</th>
            <th>SHA-256</th>
            <th>Rozmiar</th>
            <th>Status</th>
            <th>Opublikowano</th>
          </tr>
        </thead>
        <tbody>
          {this.state.releases.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center text-muted">
                Brak opublikowanych wydań.
              </td>
            </tr>
          ) : (
            this.state.releases.map(release => (
              <tr key={String(this.releaseId(release))}>
                <td>
                  <strong>{this.releaseName(release)}</strong>
                  {release.notes && (
                    <React.Fragment>
                      <br />
                      <small>{release.notes}</small>
                    </React.Fragment>
                  )}
                </td>
                <td>
                  {release.hardware ||
                    release.hardware_version ||
                    release.hw_version ||
                    "—"}
                </td>
                <td title={release.sha256 || release.hash || ""}>
                  <code>{this.shortHash(release.sha256 || release.hash)}</code>
                </td>
                <td>
                  {this.formatSize(
                    release.size !== undefined
                      ? release.size
                      : release.size_bytes
                  )}
                </td>
                <td>{this.statusLabel(release.status)}</td>
                <td>
                  {this.formatDate(
                    release.published_at || release.created_at || release.created
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    );
  }

  renderAssignmentTable() {
    return (
      <Table striped hover>
        <thead>
          <tr>
            <th />
            <th>Urządzenie</th>
            <th>Główny właściciel</th>
            <th>Wydanie / tryb</th>
            <th>Status</th>
            <th>Błąd / próby</th>
            <th>Czas</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {this.state.assignments.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center text-muted">
                Brak przypisań OTA.
              </td>
            </tr>
          ) : (
            this.state.assignments.map(assignment => {
              const id = String(this.assignmentId(assignment));
              const cancellable = this.isCancellable(assignment);
              const device = this.state.devices.find(
                candidate =>
                  String(this.deviceId(candidate)) ===
                  String(assignment.device_id)
              );
              const deviceName =
                assignment.device_desc ||
                assignment.device_name ||
                (device ? this.deviceName(device) : "") ||
                "—";
              return (
                <tr key={id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Wybierz przypisanie ${id}`}
                      checked={
                        this.state.selectedAssignmentIds.indexOf(id) >= 0
                      }
                      disabled={!cancellable}
                      onChange={() =>
                        this.toggleSelection("selectedAssignmentIds", id)
                      }
                    />
                  </td>
                  <td>
                    <strong>
                      {deviceName}
                    </strong>
                    {assignment.device_id && (
                      <React.Fragment>
                        <br />
                        <small>ID: {assignment.device_id}</small>
                      </React.Fragment>
                    )}
                  </td>
                  <td>
                    <strong>
                      {assignment.owner_name ||
                        assignment.owner_login ||
                        "—"}
                    </strong>
                    {assignment.owner_name && assignment.owner_login && (
                      <React.Fragment>
                        <br />
                        <small>Login: {assignment.owner_login}</small>
                      </React.Fragment>
                    )}
                  </td>
                  <td>
                    {assignment.version ||
                      assignment.release_version ||
                      assignment.release_id ||
                      "—"}
                    <br />
                    <small>
                      {String(assignment.mode).toLowerCase() === "urgent"
                        ? "PILNE"
                        : "normalne"}
                    </small>
                  </td>
                  <td>{this.statusLabel(assignment.status)}</td>
                  <td>
                    {assignment.error || assignment.last_error || "—"}
                    <br />
                    <small>
                      Próby:{" "}
                      {assignment.attempts !== undefined
                        ? assignment.attempts
                        : assignment.attempt_count || 0}
                    </small>
                  </td>
                  <td>
                    Utworzono:{" "}
                    {this.formatDate(
                      assignment.created_at || assignment.assigned_at
                    )}
                    <br />
                    <small>
                      Aktualizacja:{" "}
                      {this.formatDate(
                        assignment.updated_at ||
                          assignment.last_attempt_at ||
                          assignment.completed_at
                      )}
                    </small>
                  </td>
                  <td>
                    {cancellable && (
                      <Button
                        bsSize="small"
                        bsStyle="warning"
                        disabled={Boolean(this.state.action)}
                        onClick={() => this.cancelAssignments([id])}
                      >
                        Wstrzymaj / anuluj
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    );
  }

  render() {
    const busy = Boolean(this.state.action) || this.state.loading;
    return (
      <div className="content">
        <Container fluid>
          {this.state.error && (
            <ViewportAlert
              bsStyle="danger"
              onDismiss={() => this.setState({ error: "" })}
            >
              {this.state.error}
            </ViewportAlert>
          )}
          {this.state.success && (
            <ViewportAlert
              bsStyle="success"
              onDismiss={() => this.setState({ success: "" })}
            >
              {this.state.success}
            </ViewportAlert>
          )}

          <Row>
            <Col md={12}>
              <Card
                title="Aktualizacje firmware"
                category="Publikowanie podpisanych wydań i kontrolowane wdrożenia OTA"
                content={
                  <div>
                    <Button
                      bsStyle="info"
                      onClick={this.loadData}
                      disabled={busy}
                    >
                      {this.state.loading ? "Pobieranie…" : "Odśwież dane"}
                    </Button>
                  </div>
                }
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Opublikuj wydanie"
                category="Wymagane są firmware .bin i podpisany manifest .json"
                content={
                  <Form onSubmit={this.publish}>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <ControlLabel>Firmware BIN</ControlLabel>
                          <FormControl
                            type="file"
                            accept=".bin,application/octet-stream"
                            inputRef={input => {
                              this.firmwareInput = input;
                            }}
                            onChange={event =>
                              this.setState({
                                firmwareFile: event.target.files[0] || null,
                                error: ""
                              })
                            }
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <ControlLabel>Podpisany manifest JSON</ControlLabel>
                          <FormControl
                            type="file"
                            accept=".json,application/json"
                            inputRef={input => {
                              this.manifestInput = input;
                            }}
                            onChange={event =>
                              this.setState({
                                manifestFile: event.target.files[0] || null,
                                error: ""
                              })
                            }
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <ControlLabel>Notatki (opcjonalnie)</ControlLabel>
                          <FormControl
                            value={this.state.notes}
                            onChange={event =>
                              this.setState({ notes: event.target.value })
                            }
                            maxLength={1000}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Alert bsStyle="warning">
                      Pliki zostaną opublikowane dopiero po weryfikacji przez
                      serwer. Nie przesyłaj niepodpisanych obrazów firmware.
                    </Alert>
                    <Button
                      type="submit"
                      bsStyle="primary"
                      disabled={busy}
                    >
                      {this.state.action === "publish"
                        ? "Publikowanie…"
                        : "Opublikuj wydanie"}
                    </Button>
                  </Form>
                }
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Wydania"
                category={`${this.state.releases.length} pozycji`}
                ctTableFullWidth
                ctTableResponsive
                content={this.renderReleaseTable()}
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Zaplanuj wdrożenie"
                category="Jawny wybór urządzeń pozwala rozpocząć od małej grupy canary"
                content={
                  <Form onSubmit={this.requestAssignment}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <ControlLabel>Wydanie</ControlLabel>
                          <FormControl
                            componentClass="select"
                            value={this.state.selectedReleaseId}
                            onChange={event =>
                              this.setState({
                                selectedReleaseId: event.target.value
                              })
                            }
                            required
                          >
                            <option value="">Wybierz wydanie</option>
                            {this.state.releases.map(release => (
                              <option
                                value={this.releaseId(release)}
                                key={String(this.releaseId(release))}
                              >
                                {this.releaseName(release)}
                              </option>
                            ))}
                          </FormControl>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <ControlLabel>Tryb wdrożenia</ControlLabel>
                          <FormControl
                            componentClass="select"
                            value={this.state.rolloutMode}
                            onChange={event =>
                              this.setState({ rolloutMode: event.target.value })
                            }
                          >
                            <option value="normal">Normalne</option>
                            <option value="urgent">Pilne</option>
                          </FormControl>
                        </FormGroup>
                      </Col>
                    </Row>
                    <ControlLabel>
                      Urządzenia ({this.state.selectedDeviceIds.length} wybranych)
                    </ControlLabel>
                    <div style={{ marginBottom: 8 }}>
                      <Button
                        bsSize="small"
                        type="button"
                        onClick={this.selectAllDevices}
                        disabled={this.state.devices.length === 0}
                      >
                        {this.state.selectedDeviceIds.length ===
                        this.state.devices.length
                          ? "Wyczyść wybór"
                          : "Wybierz wszystkie"}
                      </Button>
                    </div>
                    <div
                      style={{
                        border: "1px solid #e3e3e3",
                        borderRadius: 4,
                        maxHeight: 240,
                        overflowY: "auto",
                        padding: "6px 12px",
                        marginBottom: 15
                      }}
                    >
                      {this.state.devices.length === 0 ? (
                        <p className="text-muted">Brak dostępnych urządzeń.</p>
                      ) : (
                        this.state.devices.map(device => {
                          const id = String(this.deviceId(device));
                          return (
                            <div className="checkbox" key={id}>
                              <input
                                id={`firmware-device-${id}`}
                                type="checkbox"
                                checked={
                                  this.state.selectedDeviceIds.indexOf(id) >= 0
                                }
                                onChange={() =>
                                  this.toggleSelection("selectedDeviceIds", id)
                                }
                              />
                              <label htmlFor={`firmware-device-${id}`}>
                                {this.deviceName(device)} (ID: {id})
                                {(device.hw_version || device.hardware) &&
                                  ` — HW: ${device.hw_version ||
                                    device.hardware}`}
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {this.state.rolloutMode === "urgent" && (
                      <Alert bsStyle="danger">
                        Tryb pilny może rozpocząć aktualizację bez standardowego
                        oczekiwania. Przed wysłaniem będzie wymagane dodatkowe
                        potwierdzenie.
                      </Alert>
                    )}
                    <Button type="submit" bsStyle="primary" disabled={busy}>
                      {this.state.action === "assign"
                        ? "Planowanie…"
                        : "Zaplanuj wdrożenie"}
                    </Button>
                  </Form>
                }
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Przypisania OTA"
                category="Status, błędy, liczba prób i historia czasowa"
                ctTableFullWidth
                ctTableResponsive
                content={
                  <div>
                    {this.renderAssignmentTable()}
                    <div style={{ padding: "0 15px 15px" }}>
                      <Button
                        bsStyle="warning"
                        disabled={
                          busy ||
                          this.state.selectedAssignmentIds.length === 0
                        }
                        onClick={() =>
                          this.cancelAssignments(
                            this.state.selectedAssignmentIds
                          )
                        }
                      >
                        Wstrzymaj / anuluj wybrane (
                        {this.state.selectedAssignmentIds.length})
                      </Button>
                    </div>
                  </div>
                }
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Kod parowania"
                category="Jednorazowy kod dla wybranego urządzenia"
                content={
                  <Form onSubmit={this.generatePairing}>
                    <Row>
                      <Col sm={7} md={5}>
                        <FormGroup>
                          <ControlLabel>Urządzenie</ControlLabel>
                          <FormControl
                            componentClass="select"
                            value={this.state.pairingDeviceId}
                            onChange={event =>
                              this.setState({
                                pairingDeviceId: event.target.value,
                                pairingCode: "",
                                pairingExpiresAt: ""
                              })
                            }
                          >
                            <option value="">Wybierz urządzenie</option>
                            {this.state.devices.map(device => (
                              <option
                                value={this.deviceId(device)}
                                key={String(this.deviceId(device))}
                              >
                                {this.deviceName(device)} (ID:{" "}
                                {this.deviceId(device)})
                              </option>
                            ))}
                          </FormControl>
                        </FormGroup>
                      </Col>
                      <Col sm={5} md={3}>
                        <Button
                          type="submit"
                          bsStyle="info"
                          disabled={busy || !this.state.pairingDeviceId}
                          style={{ marginTop: 25 }}
                        >
                          {this.state.action === "pairing"
                            ? "Generowanie…"
                            : "Generuj kod"}
                        </Button>
                      </Col>
                    </Row>
                    {this.state.pairingCode && (
                      <Alert bsStyle="success">
                        <strong>Kod jednorazowy: </strong>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "1.5em",
                            letterSpacing: 2
                          }}
                        >
                          {this.state.pairingCode}
                        </span>
                        <br />
                        <small>
                          Ważny do:{" "}
                          {this.formatDate(this.state.pairingExpiresAt)}
                        </small>
                      </Alert>
                    )}
                    <p className="text-muted">
                      Interfejs nie wyświetla ani nie przechowuje sekretów
                      urządzenia.
                    </p>
                  </Form>
                }
              />
            </Col>
          </Row>
        </Container>

        <Modal
          show={this.state.showUrgentConfirmation}
          onHide={() =>
            this.setState({
              showUrgentConfirmation: false,
              urgentConfirmation: ""
            })
          }
        >
          <Modal.Header closeButton>
            <Modal.Title>Potwierdź pilne wdrożenie</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert bsStyle="danger">
              Zamierzasz uruchomić pilne OTA na{" "}
              <strong>{this.state.selectedDeviceIds.length}</strong>{" "}
              urządzeniach. Może to przerwać ich bieżącą pracę.
            </Alert>
            <FormGroup>
              <ControlLabel>
                Wpisz <strong>PILNE</strong>, aby potwierdzić
              </ControlLabel>
              <FormControl
                autoFocus
                value={this.state.urgentConfirmation}
                onChange={event =>
                  this.setState({ urgentConfirmation: event.target.value })
                }
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() =>
                this.setState({
                  showUrgentConfirmation: false,
                  urgentConfirmation: ""
                })
              }
            >
              Wróć
            </Button>
            <Button
              bsStyle="danger"
              disabled={this.state.urgentConfirmation !== "PILNE"}
              onClick={this.assign}
            >
              Uruchom pilne wdrożenie
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default Firmware;



// WEBPACK FOOTER //
// ./src/views/Firmware.jsx